/**
 * As cenas do Modo História, passadas uma fala por vez.
 *
 * Mora fora da tela da campanha porque não é dela: as cenas acontecem NA
 * BATALHA — a abertura antes do primeiro turno, o desfecho depois do último —,
 * e a lista de capítulos só escolhe o capítulo. Foi assim que a abertura
 * passava sobre a lista de capítulos, com um fundo de floresta tapando o mapa
 * em que a conversa estava acontecendo.
 *
 * Duas formas, e a cena diz qual (ver `isCinematicScene`):
 *
 *   diálogo/narração   caixa de fala por cima do tabuleiro, sem tapá-lo
 *   imagem/vídeo       a tela inteira, que é para isso que foram escolhidas
 */

import { useCallback, useEffect, useState } from 'react';
import { ASSETS_BASE } from '../assets/manifest.js';
import { isCinematicScene, type Scene } from './story.js';
import { loadConfirmButton, useGamepad, type ConfirmButton } from './useGamepad.js';

/**
 * O retrato de quem fala.
 *
 * Uma folha de `faces` guarda oito rostos numa grade 4×2, e o índice diz qual.
 * Recortar por CSS evita gerar oito arquivos por folha só para mostrar um rosto.
 */
function Portrait({ sheet, index }: { sheet: string; index: number }): JSX.Element {
  const column = index % 4;
  const row = Math.floor(index / 4);

  return (
    <div
      className="cutscene-portrait"
      style={{
        backgroundImage: `url(${ASSETS_BASE}/img/faces/${sheet}.webp)`,
        backgroundSize: '400% 200%',
        backgroundPosition: `${(column / 3) * 100}% ${(row / 1) * 100}%`,
      }}
      aria-hidden="true"
    />
  );
}

/**
 * Passa as cenas de um momento, uma por vez.
 *
 * Avança com clique, Espaço ou Enter, e sai com Esc — o vocabulário de qualquer
 * jogo com diálogo. Quem já leu não deveria precisar procurar um botão.
 */
export function CutscenePlayer({
  scenes,
  onFinish,
  confirmButton,
}: {
  readonly scenes: readonly Scene[];
  readonly onFinish: () => void;
  /**
   * Qual botão do controle o jogador escolheu como "confirmar".
   *
   * Serve para o rótulo da dica, e não para decidir o que avança a fala:
   * QUALQUER botão de rosto avança (ver o gancho do controle, abaixo). A
   * batalha já sabe a preferência e a repassa; quem chama de fora dela lê a
   * que ficou guardada.
   */
  readonly confirmButton?: ConfirmButton | undefined;
}): JSX.Element | null {
  const [at, setAt] = useState(0);
  const scene = scenes[at];
  const [storedConfirm] = useState(loadConfirmButton);

  /*
   * Avançar não decide sozinho que a cena acabou.
   *
   * O aviso de fim saía de dentro do atualizador do `useState`, e o React pode
   * chamar um atualizador mais de uma vez para o mesmo clique — a última fala
   * avisava duas vezes que a cena tinha terminado. Aqui o atualizador só conta,
   * e quem repara que a conta passou do fim é o efeito abaixo.
   */
  const advance = useCallback(() => {
    setAt((current) => current + 1);
  }, []);

  useEffect(() => {
    if (at >= scenes.length) onFinish();
  }, [at, scenes.length, onFinish]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === ' ' || event.key === 'Enter' || event.key === 'z' || event.key === 'x') {
        event.preventDefault();
        advance();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        onFinish();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [advance, onFinish]);

  /*
   * No controle, QUALQUER botão de rosto avança a fala.
   *
   * Antes o avanço estava preso ao botão "confirmar" e o "cancelar" pulava a
   * cena inteira. Quem tivesse a preferência trocada — um adaptador genérico,
   * um controle de terceiros, o padrão errado para aquele controle — apertava
   * o que achava ser confirmar e a conversa inteira sumia de uma vez: da
   * cadeira, isso é exatamente "o diálogo não avança pelo joystick".
   *
   * Numa caixa de texto não há o que escolher, então não há o que um botão
   * possa cancelar: os quatro avançam, e pular é um gesto à parte — Start no
   * controle, Esc no teclado, ou o botão que está na tela.
   */
  useGamepad(
    {
      onAnyFaceButton: advance,
      onEndPhase: onFinish,
    },
    true,
    confirmButton ?? storedConfirm,
  );

  if (!scene) return null;

  const cinematic = isCinematicScene(scene);
  // O fundo declarado só entra na cena que toma a tela: por cima do tabuleiro
  // ele taparia justamente o mapa que a conversa está comentando.
  const background = cinematic && scene.background
    ? `${ASSETS_BASE}/img/parallaxes/${scene.background}.webp`
    : null;

  return (
    <div
      className={`cutscene-backdrop ${cinematic ? 'cinematic' : 'on-map'}`}
      role="dialog"
      aria-modal="true"
      aria-label="Cena da história"
    >
      <div className="cutscene-stage" onClick={advance}>
        {background && (
          <div className="cutscene-bg" style={{ backgroundImage: `url(${background})` }} />
        )}

        {scene.media?.kind === 'imagem' && (
          <img className="cutscene-media" src={scene.media.url} alt="" />
        )}

        {scene.media?.kind === 'video' && (
          <video
            className="cutscene-media"
            src={scene.media.url}
            autoPlay
            playsInline
            // Sem `controls` o jogador não consegue pausar nem pular para o
            // fim, e um vídeo de dois minutos vira uma parede.
            controls
            onEnded={advance}
            // O clique no vídeo é para o vídeo (pausar, buscar); avançar a cena
            // é clicando fora dele.
            onClick={(event) => event.stopPropagation()}
          />
        )}

        <button
          type="button"
          className="cutscene-skip"
          onClick={(event) => {
            event.stopPropagation();
            onFinish();
          }}
          title="Pular a cena (Esc, ou Start no controle)"
        >
          Pular ⏩
        </button>

        {(scene.body || scene.speaker) && (
          <div className={`cutscene-box ${scene.kind === 'narracao' ? 'narration' : ''}`}>
            {scene.portrait && <Portrait sheet={scene.portrait} index={scene.portraitIndex} />}
            <div className="cutscene-text">
              {scene.speaker && <span className="cutscene-speaker">{scene.speaker}</span>}
              <p>{scene.body}</p>
            </div>
          </div>
        )}

        <div className="cutscene-progress" aria-hidden="true">
          {scenes.map((s, i) => (
            <span key={s.id} className={i <= at ? 'on' : ''} />
          ))}
        </div>
      </div>
    </div>
  );
}
